import React from "react";
import {
  FiActivity, FiAlertTriangle, FiZap, FiDroplet, FiTool,
  FiUsers, FiWind, FiAlertCircle, FiCpu,
} from "react-icons/fi";
import { MdLocalGasStation } from "react-icons/md";
import { GiFuelTank } from "react-icons/gi";
import { SectionHeader } from "../../../components/Shared";
import { scoreColor, scoreLabel, ScoreRing, MiniBar, KpiCard } from "./facilityHelpers";

const OverviewPanel = ({ d }) => {
  const s = d.summary;
  const energyDelta = (((s.energyYesterday - s.energyToday) / s.energyYesterday) * 100).toFixed(1);
  const waterDelta  = (((s.waterUsageToday - s.waterUsageYesterday) / s.waterUsageYesterday) * 100).toFixed(1);
  return (
    <div>
      {/* Health + KPIs row */}
      <div className="fm-overview-top">
        {/* Health Score */}
        <div className="fm-health-card">
          <div className="fm-health-ring">
            <ScoreRing value={s.healthScore} size={120} />
          </div>
          <div className="fm-health-info">
            <div className="fm-health-title">Overall Facility Health</div>
            <div className="fm-health-status" style={{ color: scoreColor(s.healthScore) }}>
              {scoreLabel(s.healthScore)}
            </div>
            <div className="fm-health-sub">{s.uptime} uptime | {s.totalFloors} floors | {s.totalAssets} assets</div>
          </div>
        </div>

        {/* Alert summary */}
        <div className="fm-alert-summary">
          <div className="fm-alert-item fm-alert-critical">
            <FiAlertCircle size={28} />
            <div>
              <div className="fm-alert-count">{s.criticalAlerts}</div>
              <div className="fm-alert-label">Critical Alerts</div>
            </div>
          </div>
          <div className="fm-alert-item fm-alert-warning">
            <FiAlertTriangle size={28} />
            <div>
              <div className="fm-alert-count">{s.warningAlerts}</div>
              <div className="fm-alert-label">Warnings</div>
            </div>
          </div>
          <div className="fm-alert-item fm-alert-tickets">
            <FiTool size={28} />
            <div>
              <div className="fm-alert-count">{s.openTickets}</div>
              <div className="fm-alert-label">Open Tickets</div>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Grid */}
      <div className="fm-kpi-grid">
        <KpiCard icon={FiZap}    label="Energy Today"   value={`${s.energyToday} kWh`}     sub={`${energyDelta}% vs yesterday`} color="#f59e0b" />
        <KpiCard icon={FiDroplet}label="Water Today"    value={`${s.waterUsageToday.toLocaleString()} L`} sub={`+${waterDelta}% vs yesterday`} color="#3b82f6" />
        <KpiCard icon={MdLocalGasStation} label="DG Fuel Used" value={`${s.dgFuelToday} L`} sub={`${s.dgRunHoursToday}h run today`} color="#dc2626" />
        <KpiCard icon={FiWind}   label="IAQ Index"      value={`${s.iaqIndex}/100`}          sub={s.iaqLabel}                     color="#10b981" />
        <KpiCard icon={FiUsers}  label="Occupancy"      value={`${s.occupancy}%`}             sub={`${s.totalFloors} floors active`} color="#8b5cf6" />
        <KpiCard icon={FiTool}   label="Tickets"        value={`${s.openTickets} Open`}       sub={`${s.closedTickets} closed today`} color="#ef4444" />
        <KpiCard icon={FiActivity}label="System Uptime" value={s.uptime}                       sub="Last 30 days"                   color="#667eea" />
      </div>

      {/* DG Fuel Gauge */}
      <div className="fm-dg-fuel-row">
        <div className="fm-dg-fuel-card">
          <div className="fm-dg-fuel-header">
            <div className="fm-dg-fuel-icon">
              <GiFuelTank size={22} color="#dc2626" />
            </div>
            <div>
              <div className="fm-dg-fuel-title">DG Fuel Tank Level</div>
              <div className="fm-dg-fuel-status" style={{ color: s.dgStatus === "Running" ? "#10b981" : "#6b7280" }}>
                {s.dgStatus} &nbsp;|&nbsp; {s.dgFuelLevel}% remaining
              </div>
            </div>
            <div className="fm-dg-fuel-pct" style={{ color: s.dgFuelLevel < 30 ? "#ef4444" : s.dgFuelLevel < 50 ? "#f59e0b" : "#10b981" }}>
              {s.dgFuelLevel}%
            </div>
          </div>
          <MiniBar value={s.dgFuelLevel} max={100} height={10}
            color={s.dgFuelLevel < 30 ? "#ef4444" : s.dgFuelLevel < 50 ? "#f59e0b" : "#10b981"} />
          <div className="fm-dg-fuel-meta">
            <span>Capacity: {s.dgFuelCapacity} L</span>
            <span>Used today: {s.dgFuelToday} L</span>
            <span>Yesterday: {s.dgFuelYesterday} L</span>
            <span>Run time: {s.dgRunHoursToday} hrs</span>
          </div>
        </div>
      </div>

      {/* AI Insight strip */}
      <div className="fm-ai-strip">
        <div className="fm-ai-strip-title"><FiCpu size={16}/> AI Insights</div>
        <div className="fm-ai-tips">
          {d.energy.aiTips.map((tip, i) => (
            <div key={i} className="fm-ai-tip"><span className="fm-ai-bullet">→</span>{tip}</div>
          ))}
          {d.equipment.filter(e => e.aiAlert).slice(0, 2).map((e, i) => (
            <div key={`eq-${i}`} className="fm-ai-tip fm-ai-tip-danger"><span className="fm-ai-bullet">!</span>{e.name}: {e.aiAlert}</div>
          ))}
        </div>
      </div>

      {/* Zone quick view */}
      <SectionHeader title="Zone Status at a Glance" />
      <div className="fm-zone-quick-grid">
        {d.zones.map(z => (
          <div key={z.id} className={`fm-zone-quick fm-zone-${z.status.toLowerCase()}`}>
            <div className="fm-zone-quick-name">{z.name}</div>
            <div className="fm-zone-quick-stats">
              <span>{z.temp}°C</span>
              <span>{z.humidity}%</span>
              <span>CO₂ {z.co2}</span>
            </div>
            <div className={`fm-zone-badge fm-zone-badge-${z.status.toLowerCase()}`}>{z.status}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OverviewPanel;
